import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BaseDialog } from "./dialog";
import { Button } from "@/components/ui/button";
import { getClientUploadFileTransferPreview, transferClientUploadFile } from "@/service/weexpertService";
import { toast } from "../ui/use-toast";
import { SelectWithAlert } from "../ui/select-with-alert";
import { DatePicker } from "../ui/date-picker";
import { InputWithAlert } from "../ui/input-with-alert";
import { format } from "date-fns";
import { InfoIcon, AlertCircle, FileIcon } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { Alert, AlertTitle } from "../ui/alert";
import { Badge } from "../ui/badge";

const DOCUMENT_TYPE_OPTIONS = [
  { value: "PASSPORT", label: "Paszport" },
  { value: "KARTA_POBYTU", label: "Karta pobytu" },
  { value: "DECYZJA_DO_KARTY_POBYTU", label: "Decyzja do karty pobytu" },
  { value: "VISA", label: "Wiza" }
];

const TransferClientUploadFilePopup = ({ 
  open, 
  setOpen, 
  uploadFileId, 
  onTransferComplete 
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  const [formData, setFormData] = useState({
    documentType: "",
    uniqueId: "",
    validFrom: null,
    validTo: null,
    metadata: {}
  });

  // Load transfer preview when popup opens
  useEffect(() => {
    if (open && uploadFileId) {
      loadTransferPreview();
    }
  }, [open, uploadFileId]);

  const loadTransferPreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await getClientUploadFileTransferPreview(uploadFileId);
      const previewData = response.data.data;
      setPreview(previewData);
      
      // Prefill form with suggested data
      setFormData({
        documentType: previewData.suggestedDocumentType,
        uniqueId: "",
        validFrom: new Date(previewData.suggestedValidFrom),
        validTo: null,
        metadata: {}
      });
    } catch (error) {
      console.error('Failed to load transfer preview:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się załadować podglądu transferu",
        variant: "destructive"
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    console.log('Form submitted with data:', formData);
    console.log('Preview canTransfer:', preview?.canTransfer);
    
    if (!preview?.canTransfer) {
      toast({
        title: "Błąd", 
        description: preview?.reason || "Transfer nie jest możliwy",
        variant: "destructive"
      });
      return;
    }

    // Additional validation
    if (!formData.documentType) {
      toast({
        title: "Błąd",
        description: "Wybierz typ dokumentu",
        variant: "destructive"
      });
      return;
    }

    // Show confirmation step first
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setLoading(true);
    try {
      const transferRequest = {
        documentType: formData.documentType,
        uniqueId: formData.uniqueId || undefined,
        validFrom: formData.validFrom ? format(formData.validFrom, 'yyyy-MM-dd') : undefined,
        validTo: formData.validTo ? format(formData.validTo, 'yyyy-MM-dd') : undefined,
        metadata: formData.metadata
      };

      const response = await transferClientUploadFile(uploadFileId, transferRequest);
      
      if (response.status === 501) {
        toast({
          title: "Informacja",
          description: "Transfer zostanie zaimplementowany wkrótce. Obecnie możesz ręcznie utworzyć dokument w systemie wewnętrznym.",
          variant: "default"
        });
      } else {
        toast({
          title: "Sukces", 
          description: "Plik został przeniesiony do dokumentów wewnętrznych"
        });
      }
      
      setOpen(false);
      onTransferComplete && onTransferComplete();
    } catch (error) {
      console.error('Transfer failed:', error);
      toast({
        title: "Błąd",
        description: error.response?.data?.message || "Transfer nie powiódł się",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleInputChange = (field, value) => {
    console.log('Input changed:', field, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (previewLoading) {
    return (
      <BaseDialog open={open} onOpenChange={setOpen} title="Transfer do dokumentów wewnętrznych">
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2">Ładowanie podglądu transferu...</p>
        </div>
      </BaseDialog>
    );
  }

  return (
    <BaseDialog 
      open={open} 
      onOpenChange={setOpen} 
      title="Transfer do dokumentów wewnętrznych"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        {/* File Information */}
        {preview && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileIcon className="h-4 w-4" />
              <span className="font-medium">Informacje o pliku</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Nazwa pliku:</span>
                <p>{preview.fileInfo.fileName}</p>
              </div>
              <div>
                <span className="text-gray-600">Typ:</span>
                <p>{preview.fileInfo.fileType}</p>
              </div>
              <div>
                <span className="text-gray-600">Data przesłania:</span>
                <p>{format(new Date(preview.fileInfo.uploadedAt), 'dd/MM/yyyy HH:mm')}</p>
              </div>
              <div>
                <span className="text-gray-600">Status transferu:</span>
                <Badge variant={preview.canTransfer ? "success" : "destructive"}>
                  {preview.canTransfer ? "Możliwy" : "Niemożliwy"}
                </Badge>
              </div>
            </div>
            
            {!preview.canTransfer && preview.reason && (
              <Alert className="mt-3">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Powód blokady transferu:</AlertTitle>
                <p className="text-sm">{preview.reason}</p>
              </Alert>
            )}
          </div>
        )}

        {/* Transfer Form */}
        {preview?.canTransfer && !showConfirmation && (
          <div className="space-y-4">
            <SelectWithAlert
              label="Typ dokumentu"
              value={formData.documentType}
              onChangeValue={(value) => handleInputChange('documentType', value)}
              options={DOCUMENT_TYPE_OPTIONS}
              required
            />

            <InputWithAlert
              label="Numer dokumentu (opcjonalnie)"
              value={formData.uniqueId}
              onChange={(e) => handleInputChange('uniqueId', e.target.value)}
              placeholder="np. numer paszportu"
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Data ważności od</label>
                <DatePicker
                  date={formData.validFrom}
                  setDate={(date) => handleInputChange('validFrom', date)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data ważności do</label>
                <DatePicker
                  date={formData.validTo}
                  setDate={(date) => handleInputChange('validTo', date)}
                />
              </div>
            </div>

            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertTitle>Informacja</AlertTitle>
              <p className="text-sm">
                Plik zostanie skopiowany z folderu klienta do systemu dokumentów wewnętrznych. 
                Oryginalny plik zostanie zachowany ale oznaczony jako przeniesiony.
              </p>
            </Alert>
          </div>
        )}

        {/* Confirmation Step */}
        {showConfirmation && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Potwierdzenie transferu</AlertTitle>
              <p className="text-sm">
                Czy na pewno chcesz przenieść plik do dokumentów wewnętrznych?
              </p>
            </Alert>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium mb-2">Szczegóły transferu:</h3>
              <div className="space-y-1 text-sm">
                <p><strong>Typ dokumentu:</strong> {DOCUMENT_TYPE_OPTIONS.find(opt => opt.value === formData.documentType)?.label}</p>
                {formData.uniqueId && <p><strong>Numer dokumentu:</strong> {formData.uniqueId}</p>}
                {formData.validFrom && <p><strong>Ważny od:</strong> {format(formData.validFrom, 'dd/MM/yyyy')}</p>}
                {formData.validTo && <p><strong>Ważny do:</strong> {format(formData.validTo, 'dd/MM/yyyy')}</p>}
                <p><strong>Plik:</strong> {preview?.fileInfo?.fileName}</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          {showConfirmation ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelConfirmation}
                disabled={loading}
              >
                Wstecz
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? "Przenoszenie..." : "Potwierdź transfer"}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={loading}
              >
                Anuluj
              </Button>
              {preview?.canTransfer && (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.documentType}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? "Sprawdzanie..." : "Dalej"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </BaseDialog>
  );
};

export default TransferClientUploadFilePopup;